import * as React from 'react';
import { ScrollableList } from 'nomst/Components/SharedJobCollab/scrollableList';
import ContentContainer from 'nomst/Components/SharedJobCollab/contentContainer';
import ListItem from 'nomst/Components/SharedJobCollab/listItem';
import JobCard from 'nomst/Components/JobsListView/jobCard';
import { jobStore } from 'utils/MobxDataManager/singletons';
import { observer } from 'mobx-react';
import './jobsListView.scss';

const tempJobsToShow = 5;
const defaultJobTitle = 'Xxxxxxxxxx Xxxxxxxx Xxxxxxxx';
const defaultPayRange = '$XX.Xx - $XX.Xx';

// TypeScript voodoo to specialize generic class and use in JSX
type JobsList = new () => ScrollableList<IJobSummary>;
// tslint:disable-next-line variable-name
const JobsList = ScrollableList as JobsList;

@observer
class JobsListView extends React.PureComponent {
    private _renderItemFunc: (jobData: IJobSummary, index?: number) => React.ReactNode;

    constructor(props: {}) {
        super(props);
        // cache this off so we don't create a new function each time we render,
        // messing up shallow comparisons of props
        this._renderItemFunc = ((jobData: IJobSummary, index: number) =>
            this._renderJob(jobData, index));
    }

    render(): React.ReactNode {
        const jobs = jobStore.jobsList;
        const jobCount = jobStore.jobsList?.length;
        const noJobsInlist = !jobStore.isLoadingJobs && jobs?.length === 0;

        return (
            <div className="jobs-list-view tjc-page">
                <ContentContainer>
                    { !noJobsInlist &&
                        <h1 className="jobs-list-view__heading">Jobs</h1> }
                </ContentContainer>
                <ContentContainer nopadding={true}>
                    { this._renderJobsList(jobCount, jobs, noJobsInlist) }
                </ContentContainer>
            </div>
        );
    }

    private static _createRange(lowValue?: number, highValue?: number, currency?: string): JSX.Element {
        if (!lowValue || !highValue) {
            return <span>&nbsp;</span>;
        }

        // Yes this indentation looks weird, but if the " - " doesn't have characters
        // before and after it then it'll drop the space and F up the formatting
        return <span>
            <span>
                {jobStore.formatRangeValue(lowValue, currency)}
            </span> - <span>
                {jobStore.formatRangeValue(highValue, currency)}
            </span>
        </span>;
    }

    private _renderJob(jobData: IJobSummary, index?: number): React.ReactNode {
        const jobTitle = jobData ? jobData.title : defaultJobTitle;
        const numberEmployees = jobData ?
            (jobData.numberOfEmployees || 0).toString() :
            '--';
        const range = jobData ?
            JobsListView._createRange(
                jobData.minRange,
                jobData.maxRange,
                jobData.currency) :
            defaultPayRange;

        const id = jobData ? jobData.orgJobId : null;
        const jobCode = jobData ? jobData.code : null;

        return (
            <ListItem key={id || `temp_${index}`}>
                <JobCard
                    id={id}
                    jobCode={jobCode}
                    jobTitle={jobTitle}
                    range={range}
                    numberEmployees={numberEmployees}
                    className={jobData ? '' : 'placeholder' }
                />
            </ListItem>
        );
    }

    private _renderJobsList(jobCount?: number, jobs?: Nullable<IJobSummary[]>, noJobsInlist?: boolean): React.ReactNode {
        if (noJobsInlist) {
            return (
                <div className="no-list-items">
                    <img src={require('contents/no_team.svg')} className="no-list-items__img" alt=""/>
                    <div className="no-list-items__header">
                        No Jobs Available
                    </div>
                    <div className="no-list-items__body">
                        Looks like you do not have any jobs reporting to you.
                    </div>
                </div>
            );
        }

        // build up placeholder data if none provided
        if (!jobs) {
            jobCount = jobCount || tempJobsToShow;
            jobs = Array(jobCount).fill(null);
        }

        return (
            <JobsList
                className="jobs-container"
                items={jobs}
                renderItemFunc={this._renderItemFunc}
            />
        );
    }
}

export { JobsListView };
export default JobsListView;
